import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

interface Course {
  id: number;
  name: string;
  slug: string;
}

interface User {
  id: number;
  username: string;
  real_name: string;
  role: string;
  photo: string | null;
  email: string | null;
  enrolled_courses: Course[];
}

function SearchUsersPage() {
  const { user, isLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchRole, setSearchRole] = useState<'student' | 'teacher'>('student');

  // Default searchRole on load
  useEffect(() => {
    if (isLoading || !user) return;
    setSearchRole('student'); // default to students
  }, [user, isLoading]);

  useEffect(() => {
    if (isLoading || !user) return;

    const fetchUsers = async () => {
      setIsSearching(true);
      try {
        const response = await axios.get('/elearning/api/users/', {
          params: { role: searchRole, search: searchQuery },
          withCredentials: true,
        });

        let fetchedUsers = response.data;

        // Exclude current user
        fetchedUsers = fetchedUsers.filter((u: User) => u.id !== user.id);

        setUsers(fetchedUsers);
        setError(null);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || err.message || 'Failed to fetch users';
        console.error('[SearchUsersPage] Error fetching users=', errorMessage);
        setError(errorMessage);
      } finally {
        setIsSearching(false);
      }
    };

    fetchUsers();
  }, [searchQuery, searchRole, user, isLoading]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchRole(e.target.value as 'student' | 'teacher');
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
return (
  <div className="container mx-auto p-4 max-w-3xl">
    <h1 className="text-3xl font-extrabold text-gray-900 mb-6 tracking-tight">
      Search {searchRole === 'student' ? 'Students' : 'Teachers'}
    </h1>

    <div className="mb-6 flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search by username or name..."
        className="flex-1 p-3 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition"
      />
      <select
        value={searchRole}
        onChange={handleRoleChange}
        className="p-3 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition"
      >
        <option value="student">Students</option>
        <option value="teacher">Teachers</option>
      </select>
    </div>

    {error && (
      <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-xl shadow-sm">{error}</div>
    )}

    {isSearching ? (
      <div className="text-center text-gray-600 py-6">Searching...</div>
    ) : (
      <div className="space-y-4">
        {users.length === 0 && (
          <p className="text-gray-600 text-center py-4">
            {searchQuery
              ? `No ${searchRole}s found for "${searchQuery}".`
              : `No ${searchRole}s available.`}
          </p>
        )}

        {users.map((u) => (
          <div
            key={u.id}
            className="flex flex-col sm:flex-row items-center p-4 bg-white rounded-2xl shadow hover:shadow-lg transition gap-4"
          >
            {u.photo ? (
              <img
                src={u.photo}
                alt={u.real_name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold text-gray-600">
                {u.real_name ? u.real_name[0] : u.username[0]}
              </div>
            )}
            <div className="flex-1">
              <p className="text-gray-900 font-semibold text-lg">{u.real_name || 'No name provided'}</p>
              <p className="text-gray-500">@{u.username}</p>

              {searchRole === 'student' && u.enrolled_courses && u.enrolled_courses.length > 0 && (
                <div className="mt-2">
                  <p className="text-gray-900 font-medium">Enrolled Courses:</p>
                  <ul className="list-disc pl-5 text-gray-800 space-y-1">
                    {u.enrolled_courses.map((course) => (
                      <li key={course.id} className="flex items-center justify-between">
                        <span>{course.name}</span>
                        <a
                          href={`/manage_students/${course.id}`}
                          className="ml-2 text-blue-600 hover:underline text-sm"
                        >
                          Manage
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}

    <a
      href="/"
      className="mt-6 inline-block px-5 py-3 bg-gray-200 text-gray-900 rounded-xl hover:bg-gray-300 shadow-sm transition"
    >
      Back to Home
    </a>
  </div>
);

}

export default SearchUsersPage;
